// src/pages/admin/Colors.jsx - MOBILE RESPONSIVE FIXED
import React, { useState, useEffect } from 'react';
import { useOptions } from '../../context/OptionsContext';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete, Check, Close, Refresh, ColorLens, MoreVert } from '@mui/icons-material';

const Colors = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { colors, loading, fetchAllOptions } = useOptions();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [formData, setFormData] = useState({ name: '', hexCode: '', displayOrder: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  const handleMenuOpen = (event, color) => {
    setAnchorEl(event.currentTarget);
    setSelectedColor(color);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (color = null) => {
    if (color) {
      setEditingColor(color);
      setFormData({ 
        name: color.name, 
        hexCode: color.hexCode || '', 
        displayOrder: color.displayOrder || 0 
      });
    } else {
      setEditingColor(null);
      setFormData({ name: '', hexCode: '', displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingColor(null);
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (editingColor) {
        await adminApi.updateColor(editingColor.id, formData);
        setSuccess('Color updated successfully!');
      } else {
        await adminApi.createColor(formData);
        setSuccess('Color created successfully!');
      }
      handleCloseDialog();
      fetchAllOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save color');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this color?')) {
      try {
        await adminApi.deleteColor(id);
        setSuccess('Color deleted successfully!');
        fetchAllOptions();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete color');
      }
    }
  };

  const handleToggleStatus = async (color) => {
    try {
      const updatedStatus = !color.isActive;
      await adminApi.updateColor(color.id, { isActive: updatedStatus });
      setSuccess(`Color "${color.name}" ${updatedStatus ? 'activated' : 'deactivated'}`);
      fetchAllOptions();
      handleMenuClose();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle color status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Colors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product color options ({colors.length} total)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={fetchAllOptions}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => handleOpenDialog()}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ borderRadius: 2 }}
            >
              Add Color
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {colors.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">No colors found</Typography>
            </Paper>
          ) : (
            colors.map((color) => (
              <Card key={color.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: color.hexCode || '#9e9e9e',
                            border: '1px solid',
                            borderColor: 'grey.300',
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {color.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {color.hexCode || 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, color)}>
                        <MoreVert />
                      </IconButton>
                    </Stack>
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Order: {color.displayOrder || 0}
                      </Typography>
                      <Chip
                        icon={color.isActive ? <Check /> : <Close />}
                        label={color.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={color.isActive ? 'success' : 'error'}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Name</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Hex Code</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Preview</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Display Order</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {colors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No colors found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  colors.map((color) => (
                    <TableRow key={color.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">{color.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {color.hexCode || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: color.hexCode || '#9e9e9e',
                            border: '1px solid',
                            borderColor: 'grey.300',
                          }}
                        />
                      </TableCell>
                      <TableCell>{color.displayOrder || 0}</TableCell>
                      <TableCell>
                        <Chip
                          icon={color.isActive ? <Check /> : <Close />}
                          label={color.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={color.isActive ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, color)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleOpenDialog(selectedColor); handleMenuClose(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedColor)}>
          {selectedColor?.isActive ? (
            <>
              <Close fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <Check fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedColor?.id)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editingColor ? 'Edit Color' : 'Add New Color'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Color Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Hex Code"
              value={formData.hexCode}
              onChange={(e) => setFormData({ ...formData, hexCode: e.target.value })}
              placeholder="#ffffff"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ColorLens />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={handleCloseDialog} fullWidth={isMobile}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} fullWidth={isMobile}>
            {editingColor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Colors;